
import { defineComponent, reactive, toRefs, ref, onMounted, onActivated } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import API from "@/apis/TPSAPI";
import { APIResultCode } from "@/models/enums/APIResultCode";
import _ from "lodash";
import { ElMessage } from "element-plus";
import IResManufMethodDTO from "@/models/dto/IResManufMethodDTO";
import IResKaizenItemDTO from "@/models/dto/IResKaizenItemDTO";
import { usePaginatorSetup } from "@/stores/PaginatorStore";
import ElTable from "element-plus/lib/components/table";
/** Utils */
import { delay } from "@/plugins/utility";
import IReqKaizenItemSearchCriteriaDTO from "@/models/dto/IReqKaizenItemSearchCriteriaDTO";
import { DataTableType } from "@/models/enums/DataTableType";

export default defineComponent({
  emits: ["KaizenItemSelect"],
  setup(props, context) {
    const { t } = useI18n();
    const paginatorSetup = usePaginatorSetup();
    const tableType = DataTableType.kaizenItemSearchTable;

    const state = reactive({
      isTableLoading: false,
      tableRadio: undefined as number | undefined,

      // 工藝類別選項
      allManufMethodList: [] as IResManufMethodDTO[],
      isManufMethodListLoading: false,

      // 改善事項清單
      displayKaizenList: [] as IResKaizenItemDTO[],

      //搜尋改善事項參數
      searchCriteria: {
        page: 1,
        pageSize: 10,
        manufMethodIds: [],
      } as IReqKaizenItemSearchCriteriaDTO,

      filteredTotal: 0,
      colDisplayCtrl: new Array(50).fill(true) as boolean[],
    });

    //#region 建立表單ref與Validator
    const kaizenItemTableRef = ref<InstanceType<typeof ElTable>>();

    const teamNumSearchValidator = async (source: any, value: any, callback: any) => {
      const filed = source.field as "teamNum";
      //console.log(`validate(uint) : ${source.field}`);
      if (!value) {
        state.searchCriteria[filed] = undefined;
        return;
      }

      var inputNum = Number(value);
      if (Number.isNaN(inputNum)) {
        callback(new Error(t("validation_msg.invalid_uint_value")));
        return;
      }

      if (inputNum <= 0) {
        callback(new Error(t("validation_msg.invalid_uint_value")));
        return;
      }

      state.searchCriteria[filed] = Math.round(inputNum);
      callback();
    };

    const searchCriteriaFormRef = ref();
    const searchCriteriaFormRules = ref({
      teamNum: [{ validator: teamNumSearchValidator, trigger: "blur" }],
    });
    //#endregion

    //#region Hook functions
    onMounted(async () => {
      //工藝類別選項
      await getAllManufMethods();
      //取得改善事項清單
      await searchKaizenItem();
    });
    onActivated(async () => {
      //工藝類別選項
      await getAllManufMethods();
      //取得改善事項清單
      await searchKaizenItem();
    });
    //#endregion

    //#region UI Events
    /** 按下清除按鈕 */
    const onClearSearchKaizenClicked = () => {
      state.searchCriteria.pjcode = undefined;
      state.searchCriteria.creator = undefined;
      state.searchCriteria.teamNum = undefined;
      state.searchCriteria.pn = undefined;
      state.searchCriteria.startDate = undefined;
      state.searchCriteria.endDate = undefined;
      state.searchCriteria.manufMethodIds.length = 0;

      //清除table排序
      kaizenItemTableRef.value?.clearSort();
    };

    /** 顯示工藝類別下拉選單時 */
    const onManufMethodListShown = async () => {
      await getAllManufMethods();
    };

    /** 按下搜尋按鈕 */
    const onSearchKaizenClicked = async () => {
      try {
      

        //清除table排序
        kaizenItemTableRef.value?.clearSort();

        /* 取得改善事項清單 */
        await searchKaizenItem();
      } catch (error) {
        console.error(error);
      }
    };

    /** 按下並放開Enter鍵 */
    const onInputFieldEnterKeyUp = async () => {
      console.log("onInputFieldEnterKeyUp");

      /* 取得改善事項清單 */
      await searchKaizenItem();
    };

    /** 當改善事項被選擇時 */
    const onKaizenItemSelectChanged = async (kaizenItem: IResKaizenItemDTO) => {
      if (!kaizenItem) return;
      console.log(`onKaizenItemSelectChanged.  ${kaizenItem.id}, ${kaizenItem.kaizenCode}`);
      state.tableRadio = kaizenItem.id;
      context.emit("KaizenItemSelect", kaizenItem);
    };

    /** 改變排序 */
    const onSortChanged = (sort: any) => {
      console.log(`onSortChanged: ${sort.column}, ${sort.prop}, ${sort.order}`);
      state.searchCriteria.sortColName = sort.prop;
      state.searchCriteria.sortOrder = sort.order;
      searchKaizenItem();
    };

    /** 分頁輸入 */
    const onPagesizeChanged = () => {
      /* 取得改善事項清單 */
      searchKaizenItem();
    };

    const onCurrentPageChanged = () => {
      /* 取得改善事項清單 */
      searchKaizenItem();
    };

    /** 關閉欄位顯示設定Panel時 */
    const onColDisplayCtrlPanelHidded = async () => {
      await API.patchTableDisplayPreference(tableType, { displayMap: state.colDisplayCtrl });
    };
    //#endregion

    //#region Private Functions
    /** 供外部呼叫 : 當顯示dialog的時候會呼叫此方法，用以重置搜尋結果 */
    const resetSearch = async () => {
      try {
        state.isTableLoading = true;
        await updateUserPreference();
        await getAllManufMethods();
        await searchKaizenItem();
        state.tableRadio = undefined;
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };

    /** 更新使用者設置偏好 */
    const updateUserPreference = async () => {
      const getPrefResponse = await API.getTableDisplayPreference(tableType);
      if (getPrefResponse.data.result !== 1) {
        console.log(`updateUserPreference : ${getPrefResponse.data.msg}`);
      }
      state.colDisplayCtrl = getPrefResponse.data.content;
    };

    /** 呼叫API, 取得所有工藝類別 */
    async function getAllManufMethods() {
      try {
        /* Table Loading Flag 開啟 */
        state.isManufMethodListLoading = true;

        /* 若 開發時 等待 1 秒 */
        if (process.env.VUE_APP_RUN_ENV === "DEV") await delay(1000);
        const getResult = await API.getManufMethods();

        if (getResult.data.result !== APIResultCode.success) {
          throw new Error(getResult.data.msg);
        }

        state.allManufMethodList = getResult.data.content;
      } catch (error: any) {
        console.error(error);
        ElMessage({
          type: "error",
          message: t("errorMessage"),
        });
      } finally {
        /* Table Loading Flag 關閉 */
        state.isManufMethodListLoading = false;
      }
    }

    /** 呼叫API, 取得所有改善事項清單 */
    async function searchKaizenItem() {
      try {
        /* 檢查欄位 */
        await searchCriteriaFormRef.value.validate();
      } catch (error) {
        return;
      }

      try {
        /* Table Loading Flag 開啟 */
        state.isTableLoading = true;

        const getResult = await API.searchKaizenItem(state.searchCriteria);
        if (getResult.data.result !== APIResultCode.success) {
          throw new Error(getResult.data.msg);
        }

        //取得改善事項清單
        state.displayKaizenList = getResult.data.content.pageItems;

        //更新分頁資料總數
        state.filteredTotal = getResult.data.content.totalItems;
      } catch (error: any) {
        console.error(error);
        ElMessage({
          type: "error",
          message: t("errorMessage"),
        });
      } finally {
        state.isTableLoading = false;
      }
    }
    //#endregion

    return {
      ...toRefs(state),
      paginatorSetup,
      t,
      searchCriteriaFormRef,
      searchCriteriaFormRules,

      // function
      onClearSearchKaizenClicked,
      onSearchKaizenClicked,
      onPagesizeChanged,
      onCurrentPageChanged,
      onSortChanged,
      onManufMethodListShown,
      onInputFieldEnterKeyUp,
      onKaizenItemSelectChanged,
      onColDisplayCtrlPanelHidded,
      resetSearch,
    };
  },
});
